"use client";

import React from "react";
import { Control, Controller, FieldErrors, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { format, parseISO, isValid } from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import { es } from "date-fns/locale/es";
import { Clock } from "lucide-react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { AlertCircle } from "lucide-react";
import type { Field, Slot } from "@/lib/types/booking";
import type { InquiryFormData } from "./types";
import type { Pricing } from "@/lib/types/booking";
import { INPUT_BASE, SELECT_BASE } from "./constants";
import "react-datepicker/dist/react-datepicker.css";

interface BookingFormFieldsProps {
  register: UseFormRegister<InquiryFormData>;
  control: Control<InquiryFormData>;
  setValue: UseFormSetValue<InquiryFormData>;
  errors: FieldErrors<InquiryFormData>;
  allFields: Field[];
  slots: Slot[];
  availableToOptions?: string[];
  horario: string;
  horarioHasta: string;
  pricing: Pricing | null;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
}

export function BookingFormFields({
  register,
  control,
  setValue,
  errors,
  allFields,
  slots,
  availableToOptions,
  horario,
  horarioHasta,
  pricing,
  onSubmit,
}: BookingFormFieldsProps) {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language?.startsWith("en");
  const dateLocale = isEn ? enUS : es;

  // Only available "From" hours
  const availableFromOptions = React.useMemo(() => {
    return [...new Set(slots.map((s) => s.from))].sort();
  }, [slots]);

  // Contiguous available "To" hours starting from selected horario
  const toOptions = React.useMemo(() => {
    if (availableToOptions) {
      return availableToOptions;
    }
    if (!horario) return [];
    const result: string[] = [];
    let currentFrom = horario;
    while (true) {
      const match = slots.find((s) => s.from === currentFrom && s.available);
      if (!match) break;
      result.push(match.to);
      currentFrom = match.to;
    }
    return result;
  }, [availableToOptions, slots, horario]);

  return (
    <form onSubmit={onSubmit} className="min-w-0 space-y-4">
      <div className="form-field min-w-0 pb-3">
        <label htmlFor="fecha" className="form-label">
          {t("form.fecha")}
        </label>
        <Controller
          control={control}
          name="fecha"
          render={({ field }) => {
            const value =
              field.value &&
              typeof field.value === "string" &&
              field.value.length
                ? parseISO(field.value)
                : null;
            const selected = value && isValid(value) ? value : null;
            return (
              <DatePicker
                id="fecha"
                selected={selected}
                onChange={(date: Date | null) =>
                  field.onChange(date ? format(date, "yyyy-MM-dd") : "")
                }
                minDate={new Date()}
                locale={dateLocale}
                dateFormat={isEn ? "MM/dd/yyyy" : "dd/MM/yyyy"}
                placeholderText={t("form.selectDate")}
                className={INPUT_BASE}
                wrapperClassName="w-full"
                calendarClassName="big-sport-datepicker"
              />
            );
          }}
        />
      </div>

      <div className="form-field min-w-0 pb-3">
        <label htmlFor="cancha" className="form-label">
          {t("form.cancha")}
        </label>
        <select id="cancha" {...register("cancha")} className={SELECT_BASE}>
          <option value="">{t("form.selectCourt")}</option>
          {allFields.map((f) => (
            <option key={f.fieldId} value={String(f.fieldId)}>
              {f.fieldName}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
        <div className="form-field min-w-0 pb-3">
          <label htmlFor="horario" className="form-label">
            {t("form.desde")}
          </label>
          <select id="horario" {...register("horario")} className={SELECT_BASE}>
            <option value="">{t("form.selectHour")}</option>
            {availableFromOptions.map((from) => (
              <option key={from} value={from}>
                {from}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field min-w-0 pb-3">
          <label htmlFor="horarioHasta" className="form-label">
            {t("form.hasta")}
          </label>
          <select
            id="horarioHasta"
            {...register("horarioHasta")}
            className={SELECT_BASE}
          >
            <option value="">{t("form.selectHour")}</option>
            {toOptions.map((to) => (
              <option key={to} value={to}>
                {to}
              </option>
            ))}
          </select>
        </div>
      </div>

      {pricing && (
        <div className="min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50/80 p-3 shadow-sm sm:rounded-xl sm:p-4">
          <div className="flex min-w-0 flex-wrap items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <Clock className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-slate-500">
                  {t("summary.reserva")}
                </p>
                <p className="text-sm sm:text-base font-semibold text-slate-800">
                  {horario} – {horarioHasta}
                  <span className="ml-1.5 font-normal text-slate-500">
                    ({pricing.hours}{" "}
                  {pricing.hours === 1 ? t("form.hourShort") : t("form.hoursShort")})
                  </span>
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-slate-500">
                {t("form.total")}
              </p>
              <p className="text-lg sm:text-xl font-bold text-primary">
                ${pricing.totalAmount?.toLocaleString() ?? "0"}
              </p>
            </div>
          </div>
        </div>
      )}

      <PhoneField
        register={register}
        setValue={setValue}
        errors={errors}
      />

      <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="form-field min-w-0 pb-3">
          <label htmlFor="nombre" className="form-label">
            {t("form.nombre")}
          </label>
          <input
            id="nombre"
            {...register("nombre")}
            placeholder={t("form.placeholderName")}
            className={INPUT_BASE}
          />
        </div>
        <div className="form-field min-w-0 pb-3">
          <label htmlFor="email" className="form-label">
            {t("form.email")}
          </label>
          <input
            id="email"
            {...register("email")}
            type="email"
            placeholder={t("form.placeholderEmail")}
            className={INPUT_BASE}
          />
        </div>
      </div>

      <div className="min-w-0 pb-3">
        <button
          type="submit"
          className="w-full min-w-0 bg-primary text-white py-3 sm:py-3.5 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base shadow-md transition-all duration-200 hover:bg-primary-strong hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-2"
        >
          {t("form.confirmReservation")}
        </button>
      </div>
    </form>
  );
}

interface PhoneFieldProps {
  register: UseFormRegister<InquiryFormData>;
  setValue: UseFormSetValue<InquiryFormData>;
  errors: FieldErrors<InquiryFormData>;
}

function PhoneField({ register, setValue, errors }: PhoneFieldProps) {
  const { t } = useTranslation();

  return (
    <div className="form-field min-w-0 pb-3">
      <label htmlFor="telefono" className="form-label">
        {t("form.telefono")} (+54)
      </label>
      <div className="flex min-w-0 gap-2 sm:gap-3">
        <div className="flex h-[40px] shrink-0 items-center rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-600 sm:h-[42px] sm:rounded-xl">
          +54
        </div>
        <input
          id="telefono"
          type="tel"
          inputMode="numeric"
          placeholder={t("form.placeholderPhone")}
          maxLength={11}
          className={`min-w-0 flex-1 h-[40px] sm:h-[42px] px-3 sm:px-4 text-[14px] sm:text-[15px] font-medium text-slate-800 bg-white border-[1.5px] rounded-lg sm:rounded-xl placeholder:text-slate-400 focus:outline-none focus:ring-[3px] transition-all hover:border-slate-300 ${
            errors.telefono
              ? "border-red-400 focus:border-red-500 focus:ring-red-500/15"
              : "border-slate-200 focus:border-primary focus:ring-primary/15"
          }`}
          {...register("telefono", {
            onChange: (e) => {
              const v = e.target.value.replace(/\D/g, "").replace(/^0+/, "").slice(0, 11);
              e.target.value = v;
              setValue("telefono", v, { shouldValidate: true });
            },
          })}
        />
      </div>
      <p className="mt-1.5 text-[11px] sm:text-xs text-slate-500">
        {t("form.areaCodeHint")}
      </p>
      {errors.telefono && (
        <div
          role="alert"
          className="mt-2 flex gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 sm:px-4 sm:py-3"
        >
          <AlertCircle
            className="h-5 w-5 shrink-0 text-red-500 sm:h-5 sm:w-5"
            aria-hidden
          />
          <p className="text-sm font-medium text-red-800 whitespace-pre-line leading-relaxed">
            {errors.telefono.message}
          </p>
        </div>
      )}
    </div>
  );
}
